export interface ApiResponse<T> {
  result: T;
  resultType: string;
}

export interface PageModelResponse<T> {
  total: number;
  pageSize: number;
  pageNo: number;
  items: T[];
}

export interface TokenViewModel {
  isAuthenticated?: boolean;
  accessToken?: string;
}

export interface SystemConfigViewModel {
  authenticationAuthority: string;
  liveDocsApiEndPoints: {
    primary: string;
    sandbox: string;
  };
}

export interface StatusViewModel {
  isAuthenticated?: boolean;
  developerName?: string;
  teamId?: string;
  roles?: string[];
}

export type ProjectEnvironment = 'primary' | 'sandbox';

export interface ProjectViewModel {
  id: string;
  name: string;
  environment: ProjectEnvironment;
}

export type CredentialType = 'apiKey' | 'oAuthClient';
export type AuthFlow = 'clientCredentials' | 'authorizationCode' | 'implicit';

export interface CredentialViewModel {
  id: string;
  credentialType: CredentialType;
  flow: AuthFlow;
  authClientId: string;
  secrets: SecretViewModel[];
  redirectUris: string[];
  allowedScopes: string[];
}

export interface SecretViewModel {
  id: string;
  description: string;
  secret?: string;
}

export interface PackageViewModel {
  id: string;
  operations: string[];
}

async function makeRequest<TRequest, TResponse>(
  url: string,
  accessToken?: string,
  method = 'get',
  postData?: TRequest,
): Promise<TResponse | null> {
  const fetchOptions: RequestInit = accessToken
    ? {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        method, // only set on the authed side, the only unauth request we make is to get the token
      }
    : {
        credentials: 'include',
      };

  if (postData) {
    fetchOptions.body = JSON.stringify(postData);
  }

  const response = await fetch(url, fetchOptions);
  const data = (await response.json()) as ApiResponse<TResponse>;
  return data.result;
}

export async function getToken(): Promise<TokenViewModel> {
  const response = await makeRequest<never, TokenViewModel>('/api/token');
  return response ?? { isAuthenticated: false };
}

export async function getConfig(): Promise<SystemConfigViewModel> {
  const response = await makeRequest<never, SystemConfigViewModel>(
    '/api/config',
  );
  return (
    response ?? {
      authenticationAuthority: '',
      liveDocsApiEndPoints: { primary: '', sandbox: '' },
    }
  );
}

export async function getStatus(
  accessToken: string,
  gaClientId: string,
): Promise<StatusViewModel> {
  const response = await makeRequest<never, StatusViewModel>(
    `/api/status?gaClientId=${gaClientId}`,
    accessToken,
  );
  return response ?? { isAuthenticated: false };
}

export async function getProjects(
  accessToken: string,
  teamId: string,
): Promise<ProjectViewModel[]> {
  const response = await makeRequest<
    never,
    PageModelResponse<ProjectViewModel>
  >(`/api/v1/projects?pageSize=-1&filter.teamId=${teamId}`, accessToken);
  return response?.items ?? [];
}

export async function getCredentials(
  accessToken: string,
  projectId: string,
): Promise<CredentialViewModel[]> {
  const response = await makeRequest<
    never,
    PageModelResponse<CredentialViewModel>
  >(
    `/api/v1/credentials?projectId=${projectId}&credentialType=apiKey&credentialType=oAuthClient`,
    accessToken,
  );
  return response?.items ?? [];
}

export async function getCredential(
  accessToken: string,
  credentialId: string,
): Promise<CredentialViewModel | null> {
  const response = await makeRequest<never, CredentialViewModel>(
    `/api/v1/credentials/${credentialId}`,
    accessToken,
  );
  return response ?? null;
}

export async function updateCredential(
  accessToken: string,
  credential: CredentialViewModel,
): Promise<CredentialViewModel | null> {
  const response = await makeRequest<CredentialViewModel, CredentialViewModel>(
    `/api/v1/credentials/${credential.id}`,
    accessToken,
    'PUT',
    credential,
  );
  return response;
}

export async function getSecret(
  accessToken: string,
  credentialId: string,
  secretId: string,
): Promise<SecretViewModel | null> {
  const response = await makeRequest<never, SecretViewModel>(
    `/api/v1/credentials/${credentialId}/secrets/${secretId}`,
    accessToken,
  );
  return response ?? null;
}

export async function getPackages(
  accessToken: string,
  projectId: string,
): Promise<PackageViewModel[]> {
  const response = await makeRequest<
    never,
    PageModelResponse<PackageViewModel>
  >(`/api/v1/packages?pageSize=-1&filter.projectId=${projectId}`, accessToken);
  return response?.items ?? [];
}
